import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getSlackChannels,
  getMsteams,
  getMsteamChannels,
  isSlackAvailable,
  isMsteamsAvailable,
} from '../../services';

export const DEFAULT_INTEGRATION_STATE = {
  available: false,
  isFetching: false,
  error: null,
  channels: [],
  instances: [],
  activeInstanceId: null,
  meta: {},
};

const initialState = {
  isFetching: false,
  error: null,
  integrations: {},
};

const ensureIntegrationState = (integrations, id) => {
  if (!integrations[id]) {
    integrations[id] = { ...DEFAULT_INTEGRATION_STATE };
  }
};

const integrationHandlers = {
  slack: {
    checkAvailable: async (profileId) => {
      const slackResult = await isSlackAvailable(profileId);
      return Boolean(slackResult);
    },
    fetchData: async ({ token }) => {
      const res = await getSlackChannels(token);
      const channels =
        res && res.channels
          ? res.channels.filter(
              ({ applicationChannel }) => applicationChannel === true
            )
          : [];

      return {
        available: true,
        channels,
      };
    },
  },
  msteams: {
    checkAvailable: async (profileId) => {
      const msteamsResult = await isMsteamsAvailable(profileId);
      return Boolean(msteamsResult);
    },
    fetchData: async ({ token }) => {
      const res = await getMsteams(token);
      const instances = res && res.teams ? res.teams : [];

      if (!instances.length) {
        return {
          available: false,
          instances: [],
        };
      }

      const active = instances.filter(
        ({ applicationTeam }) => applicationTeam === true
      );

      let activeInstanceId = null;
      let channels = [];

      if (active && active.length) {
        const teamId = active[0].teamId;
        activeInstanceId = teamId;
        const channelsRes = await getMsteamChannels(teamId);
        if (channelsRes && channelsRes.channels && channelsRes.channels.length) {
          channels = channelsRes.channels;
        }
      }

      return {
        available: true,
        instances,
        activeInstanceId,
        channels,
      };
    },
  },
};

export const fetchIntegrations = createAsyncThunk(
  'integrations/fetchIntegrations',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { token, user } = state.auth;

      if (!token || !user || !Array.isArray(user.profiles)) {
        return rejectWithValue('Missing token or user profiles for integrations');
      }

      const userInfoProfiles = user.profiles;
      if (!userInfoProfiles.length) {
        return rejectWithValue('No profiles available for integrations');
      }

      const profileId = userInfoProfiles[0].profileId;

      const integrations = {};

      for (const [id, handler] of Object.entries(integrationHandlers)) {
        try {
          const available = await handler.checkAvailable(profileId);

          if (!available) {
            integrations[id] = {
              ...DEFAULT_INTEGRATION_STATE,
              available: false,
            };
            continue;
          }

          const data = await handler.fetchData({ token, profileId });

          integrations[id] = {
            ...DEFAULT_INTEGRATION_STATE,
            ...data,
            available:
              typeof data.available === 'boolean' ? data.available : true,
          };
        } catch (integrationError) {
          integrations[id] = {
            ...DEFAULT_INTEGRATION_STATE,
            available: false,
            error:
              integrationError && integrationError.message
                ? integrationError.message
                : 'Failed to load integration',
          };
        }
      }

      return {
        integrations,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch integrations');
    }
  }
);

const integrationsSlice = createSlice({
  name: 'integrations',
  initialState,
  reducers: {
    setIntegrationAvailable(state, action) {
      const { id, available } = action.payload;
      ensureIntegrationState(state.integrations, id);
      state.integrations[id].available = available;
    },

    setIntegrationData(state, action) {
      const { id, data } = action.payload;
      ensureIntegrationState(state.integrations, id);
      Object.assign(state.integrations[id], data);
    },

    setIntegrationActiveInstance(state, action) {
      const { id, instanceId, instanceKey = 'teamId', flagKey = 'applicationTeam' } =
        action.payload;

      ensureIntegrationState(state.integrations, id);

      const integration = state.integrations[id];

      if (!Array.isArray(integration.instances)) {
        integration.instances = [];
      }

      let activeId = instanceId;

      integration.instances = integration.instances.map((instance) => {
        if (instance[instanceKey] === instanceId) {
          if (!instance[flagKey]) {
            instance[flagKey] = true;
          } else {
            instance[flagKey] = false;
            activeId = null;
            integration.channels = [];
          }
        } else {
          instance[flagKey] = false;
        }
        return instance;
      });

      integration.activeInstanceId = activeId;
    },

    setIntegrationError(state, action) {
      const { id, error } = action.payload;
      ensureIntegrationState(state.integrations, id);
      state.integrations[id].error = error;
    },

    resetIntegration(state, action) {
      const { id } = action.payload;
      if (state.integrations[id]) {
        state.integrations[id] = { ...DEFAULT_INTEGRATION_STATE };
      }
    },

    resetAllIntegrations(state) {
      state.integrations = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntegrations.pending, (state) => {
        state.isFetching = true;
        state.error = null;
      })
      .addCase(fetchIntegrations.fulfilled, (state, action) => {
        state.isFetching = false;
        state.error = null;
        state.integrations = action.payload.integrations || {};
      })
      .addCase(fetchIntegrations.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload || action.error.message;
        state.integrations = {};
      });
  },
});

export const {
  setIntegrationAvailable,
  setIntegrationData,
  setIntegrationActiveInstance,
  setIntegrationError,
  resetIntegration,
  resetAllIntegrations,
} = integrationsSlice.actions;

export default integrationsSlice.reducer;
